import React, { useContext, useMemo } from 'react';
import { IObjektVy, IObjektVyComp } from '../../../../../../../Types/ObjektVy';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { getAllKontodelar, getAllKontodelarBen } from './Utils/KonteringUtils';
import { KonteringContext } from '../../../../../_Common/Components/KonteringManager/KonteringContext';
import FakturaDelContext from '../FakturaDelContextManager/FakturaDelContext';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import KonteringViewComp from './Components/KonteringViewComp/KonteringViewComp';
import SkeletonKontering from './Components/SkeletonKontering/SkeletonKontering';
import { useQuery } from '@apollo/client';
import TillaggsInfoQuery from './Queries/IsTillaggsInfoActiveQuery.gql';
import {
  IsTillaggsInfoActiveQuery,
  IsTillaggsInfoActiveQueryVariables,
} from './Queries/_QueryTypes/IsTillaggsInfoActiveQuery';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';

export const KonteringComp: IObjektVyComp = {
  caption: '',
  comp: () => <KonteringView />,
};

const useStyles = makeStyles()((theme: Theme) => ({
  loadingContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: '80px',
    position: 'relative',
    width: '100%',
  },
}));

const KonteringView: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { classes } = useStyles();
  const konteringContext = useContext(KonteringContext);
  const formContext = useContext(BusinessDataFormContext);
  const fakturaDelContext = useContext(FakturaDelContext);
  const { activeDatabase } = useContext(ActiveDatabaseContext);

  const tillaggsInfoQuery = useQuery<
    IsTillaggsInfoActiveQuery,
    IsTillaggsInfoActiveQueryVariables
  >(TillaggsInfoQuery, {
    variables: { dbName: activeDatabase ?? '' },
  });

  const isFIActive = useMemo(
    () =>
      tillaggsInfoQuery.data?.db?.EKTillaggsinfo_All?.find(
        tillaggsInfo => tillaggsInfo.BENAMNING === 'Förbrukningsinventarier'
      )?.AKTIV ?? false,
    [tillaggsInfoQuery.data?.db?.EKTillaggsinfo_All]
  );

  if (!formContext) {
    return (
      <span className={classes.loadingContainer}>
        <EDAvenyProgress />
      </span>
    );
  }

  if (
    formContext.data &&
    (formContext.formState.loading || formContext.mutationLoading)
  ) {
    const beslutsattestantIfOne =
      fakturaDelContext.beslutsAttestanterAllaFakturadelar?.length === 1
        ? fakturaDelContext.beslutsAttestanterAllaFakturadelar[0]
        : null;
    if (fakturaDelContext.konteringsforslag.length > 0) {
      return (
        <>
          {fakturaDelContext.konteringsforslag.map((konteringsForslag, i) => {
            const kodstrangBenamningar = getAllKontodelarBen(
              konteringsForslag?._Associations
            );
            const kodstrangDelar = getAllKontodelar(
              konteringsForslag?._Associations
            );

            const kodstrang = kodstrangDelar
              .map(kd => kd?.KONTODEL ?? '')
              .join('-');

            let fakturaBelopp = 0;
            if (!konteringsForslag.DEBET && !konteringsForslag.KREDIT) {
              fakturaBelopp = formContext.data?.FakturaBelopp2 ?? 0;
            } else {
              fakturaBelopp =
                (konteringsForslag.DEBET ?? 0) -
                (konteringsForslag.KREDIT ?? 0);
            }

            return (
              <SkeletonKontering
                key={i}
                beslutsAttestant={
                  fakturaDelContext.beslutsattestByKonteringsforslag(
                    konteringsForslag
                  ) ?? beslutsattestantIfOne
                }
                fakturaBelopp={fakturaBelopp}
                kodstrang={kodstrang}
                kodstrangBenamningar={kodstrangBenamningar}
                beloppExklMoms={konteringsForslag.BELOPP_EXKL_MOMS}
                beloppInklMoms={konteringsForslag.BELOPP_INKL_MOMS}
                radtyp={konteringsForslag.RADTYP}
              />
            );
          })}
        </>
      );
    } else {
      return (
        <SkeletonKontering
          beslutsAttestant={beslutsattestantIfOne}
          fakturaBelopp={formContext.data?.FakturaBelopp2}
          kodstrang={''}
          kodstrangBenamningar={[]}
        />
      );
    }
  }

  if (
    konteringContext.konteringValidationQueryLoading ||
    formContext.formState.loading ||
    formContext.mutationLoading
  ) {
    return (
      <span className={classes.loadingContainer}>
        <EDAvenyProgress />
      </span>
    );
  }

  if (
    !formContext.data &&
    !formContext.formState.loading &&
    !formContext.mutationLoading
  ) {
    return formContext?.error ? (
      <EDTypography>Fel vid hämtning av Konteringar</EDTypography>
    ) : (
      <EDTypography>Konteringar saknas</EDTypography>
    );
  }

  return <KonteringViewComp isFIActive={isFIActive} />;
};

export const Kontering: IObjektVy = {
  type: 'ObjektVy',
  id: 'Kontering',
  info: {},
  def: () => KonteringComp,
};

export default KonteringComp;
